import clsx from 'clsx';
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: ReactNode;
  htmlFor: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** shows a required marker after the label (the control still needs `required`) */
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('ui-field', error ? 'ui-field--invalid' : null, className)}>
      <label htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="ui-field__req" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint && <span className="ui-field__hint">{hint}</span>}
      {error && (
        <span className="ui-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={clsx('ui-input', className)} {...rest} />;
  },
);

/** Styled native select (keyboard, screen readers and mobile pickers keep working). */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return <select ref={ref} className={clsx('ui-select', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, rows = 3, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={clsx('ui-textarea', className)} {...rest} />;
});
